// Front-end credit estimator + confirmation gate for the "Generate Voiceover"
// admin buttons. This runs BEFORE the existing API dispatch so admins can see
// how many ElevenLabs v2 credits a generation will roughly cost.
//
// The character math mirrors how backend/tts_pipeline.js combines a screen's
// steps: every spoken step instruction is concatenated, with one break tag
// inserted between consecutive segments.

// Each step break uses the optimized `<break time="3s"/>` string, counted as
// exactly 17 characters by the Magnific / ElevenLabs v2 billing formula.
const STEP_BREAK_CHARS = 0   // No break anymore
const CREDITS_PER_CHAR = 0.5

/**
 * Collect the ordered list of instruction strings that will be spoken for a
 * screen, matching the backend's normalizeSteps() for the standard format.
 *
 * @param {object} formData - The admin screen's form state.
 * @returns {string[]} Raw instruction strings (may include empty entries).
 */
export function collectStepInstructions(formData) {
    if (!formData) return []

    // Hex / logicTwoContent format: code steps carry subSteps, flattened the
    // same way backend/tts_pipeline.js normalizeSteps() does so the estimate
    // matches the audio the pipeline actually produces.
    if (Array.isArray(formData.codeSteps) && formData.codeSteps.length > 0) {
        return collectHexInstructions(formData)
    }

    const instructions = (formData.steps || []).map((s) => s?.instruction || '')

    // Standard trailing step (assembly / play screens).
    if (formData.finalStepInstruction) {
        instructions.push(formData.finalStepInstruction)
    }

    return instructions
}

// Block-type → label map, mirrored from backend/tts_pipeline.js normalizeSteps.
const HEX_BLOCK_LABELS = {
    action: 'Action',
    wait: 'Wait',
    end: 'Stop',
    stop: 'Stop',
    decision: 'Decision',
    condition: 'Condition',
    loop: 'Loop',
    repeat: 'Repeat',
    sensor: 'Sensor',
    variable: 'Variable',
    if: 'If',
    end_loop: 'End Loop',
    end_condition: 'End Decision',
    end_sensor: 'End Sensor',
    end_variable: 'End Variable',
    end_if: 'End If',
}

/**
 * Flatten a hex (logicTwoContent) tutorial payload into the ordered list of
 * spoken instruction strings. This is a 1:1 port of the hex branch of
 * backend/tts_pipeline.js normalizeSteps(), including subStep expansion, so the
 * credit estimate reflects exactly what the pipeline will narrate.
 *
 * @param {object} jsonData - { codeSteps, finalStepInstruction1, finalStepInstruction2 }
 * @returns {string[]} Ordered instruction strings (one per micro-slide).
 */
export function collectHexInstructions(jsonData) {
    const flat = []
    const pushEntry = (instruction) => flat.push(instruction)

    // Entries for subSteps[0..N-2] — mirrors generateSubStepEntries.
    const generateSubStepEntries = (subSteps) => {
        for (let j = 0; j < subSteps.length - 1; j++) {
            const nextSub = subSteps[j + 1]
            if (nextSub.action === 'SELECT_READ_SOURCE') {
                const readValue = nextSub.expectedValue || ''
                pushEntry(
                    nextSub.message ||
                        'Click the Read dropdown to select a source.'
                )
                pushEntry(`"${readValue}" has been selected.`)
            } else if (nextSub.action === 'READ_SENSOR') {
                pushEntry(nextSub.message || 'Click the Read button.')
                pushEntry('Reading the sensor values...')
            } else {
                pushEntry(nextSub.message || '')
            }
        }
    }

    const codeSteps = jsonData.codeSteps || []
    for (let i = 0; i < codeSteps.length; i++) {
        const step = codeSteps[i]
        const subSteps = step.subSteps || []
        const blockLabel =
            HEX_BLOCK_LABELS[step.blockType] || step.blockType || 'Block'
        const action = step.action || 'PLACE_BLOCK'

        if (action === 'INSERT_BLOCK') {
            pushEntry(
                step.preMessage ||
                    step.message ||
                    'Click the block where you want to insert before.'
            )
            pushEntry(
                step.selectMessage ||
                    'Click the Insert (+) button above the block.'
            )
            pushEntry(`Choose the ${blockLabel} block.`)
            if (subSteps.length > 0) {
                pushEntry(
                    subSteps[0].message ||
                        `The ${blockLabel} block has been inserted.`
                )
                generateSubStepEntries(subSteps)
            } else {
                pushEntry(
                    step.message || `The ${blockLabel} block has been inserted.`
                )
            }
        } else if (action === 'DELETE_BLOCK') {
            pushEntry(
                step.preMessage ||
                    step.message ||
                    `Click the ${blockLabel} block, then click the Delete button.`
            )
            pushEntry(
                step.selectMessage || 'Click the Delete button above the block.'
            )
            pushEntry(step.message || `The ${blockLabel} block has been deleted.`)
        } else {
            // PLACE_BLOCK (default)
            pushEntry(
                step.preMessage ||
                    (i === 0
                        ? 'First, click the Select block.'
                        : 'Click the Select block again.')
            )
            pushEntry(step.selectMessage || `Choose the ${blockLabel} block.`)
            if (subSteps.length > 0) {
                pushEntry(
                    subSteps[0].message ||
                        `The ${blockLabel} block has been placed.`
                )
                generateSubStepEntries(subSteps)
            } else {
                pushEntry(
                    step.message || `The ${blockLabel} block has been placed.`
                )
            }
        }
    }

    // Final 2 instruction slides.
    if (jsonData.finalStepInstruction1) {
        pushEntry(jsonData.finalStepInstruction1)
    }
    if (jsonData.finalStepInstruction2) {
        pushEntry(jsonData.finalStepInstruction2)
    }

    return flat
}

/**
 * Apply the official Magnific API formula for ElevenLabs v2:
 *
 *   totalCharacters  = Σ(non-empty step instruction lengths) + (breaks × 17)
 *   estimatedCredits = ceil(totalCharacters × 0.1)
 *
 * @param {string[]} instructions - Raw instruction strings for the screen.
 * @returns {{ totalCharacters: number, estimatedCredits: number, stepCount: number }}
 */
export function calculateVoiceoverCredits(instructions) {
    const nonEmpty = (instructions || []).filter(
        (text) => typeof text === 'string' && text.trim().length > 0
    )

    const charTotal = nonEmpty.reduce((sum, text) => sum + text.length, 0)
    const breakCount = Math.max(0, nonEmpty.length - 1)

    const totalCharacters = charTotal + breakCount * STEP_BREAK_CHARS
    const estimatedCredits = Math.ceil(totalCharacters * CREDITS_PER_CHAR)

    return { totalCharacters, estimatedCredits, stepCount: nonEmpty.length }
}

/**
 * Intercept a "Generate Voiceover" click: estimate the cost and ask the admin
 * to confirm before any credits are spent.
 *
 * @param {object} formData - The admin screen's form state.
 * @returns {boolean} true if the admin chose to proceed, false to abort.
 */
export function confirmVoiceoverGeneration(formData) {
    const { estimatedCredits } = calculateVoiceoverCredits(
        collectStepInstructions(formData)
    )

    return window.confirm(
        `Did you click 'Save' first? The generator will use your last saved data.\n\n` +
            `Warning: Generating this voiceover will consume up to ~${estimatedCredits} credits from your balance ` +
            `(this is an upper bound — steps whose text is unchanged since the last generation are skipped and not re-billed).\n\n` +
            `Do you want to proceed?`
    )
}
